import { useId, useState } from "react";
import { Eye, EyeOff, Loader2 } from "lucide-react";

const inputClass = (invalid) =>
  `w-full h-12 px-4 rounded-xl border bg-white text-base text-brand-text placeholder:text-brand-placeholder outline-none transition-colors disabled:bg-brand-surface2 focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20 ${
    invalid ? "border-brand-accent" : "border-brand-border"
  }`;

export function TextField({ label, error, hint, ...props }) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-semibold text-brand-text mb-1.5">{label}</label>
      <input
        id={id}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : undefined}
        className={inputClass(!!error)}
        {...props}
      />
      {error ? (
        <p id={`${id}-error`} role="alert" className="mt-1.5 text-xs font-medium text-brand-accent">{error}</p>
      ) : (
        hint && <p className="mt-1.5 text-xs text-brand-textSecondary">{hint}</p>
      )}
    </div>
  );
}

export function PasswordField({ label, error, hint, disabled, labelAction, ...props }) {
  const id = useId();
  const [visible, setVisible] = useState(false);
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label htmlFor={id} className="block text-sm font-semibold text-brand-text">{label}</label>
        {labelAction}
      </div>
      <div className="relative">
        <input
          id={id}
          type={visible ? "text" : "password"}
          disabled={disabled}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
          className={`${inputClass(!!error)} pr-12`}
          {...props}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? "Hide password" : "Show password"}
          className="absolute right-0 top-0 h-12 w-12 flex items-center justify-center text-brand-textSecondary hover:text-brand-text"
        >
          {visible ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
      {error ? (
        <p id={`${id}-error`} role="alert" className="mt-1.5 text-xs font-medium text-brand-accent">{error}</p>
      ) : (
        hint && <p className="mt-1.5 text-xs text-brand-textSecondary">{hint}</p>
      )}
    </div>
  );
}

export function PrimaryButton({ loading, loadingLabel, children, ...props }) {
  return (
    <button
      type="submit"
      disabled={loading}
      className="w-full h-12 rounded-xl bg-brand-accent text-white font-semibold flex items-center justify-center gap-2 hover:bg-red-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-accent"
      {...props}
    >
      {loading && <Loader2 size={18} className="animate-spin" aria-hidden="true" />}
      {loading ? loadingLabel : children}
    </button>
  );
}

export function FormError({ children }) {
  if (!children) return null;
  return (
    <p role="alert" className="rounded-xl bg-brand-accentMuted text-brand-accent text-sm font-medium px-4 py-3">
      {children}
    </p>
  );
}

export function LinkButton({ children, ...props }) {
  return (
    <button
      type="button"
      className="min-h-[44px] px-2 text-sm font-semibold text-brand-accent hover:underline disabled:opacity-60"
      {...props}
    >
      {children}
    </button>
  );
}

export function OrDivider() {
  return (
    <div className="flex items-center gap-3 my-5" role="separator" aria-label="or">
      <span className="flex-1 h-px bg-brand-border" />
      <span className="text-xs font-medium uppercase tracking-wide text-brand-textSecondary">or</span>
      <span className="flex-1 h-px bg-brand-border" />
    </div>
  );
}

export function GoogleButton({ onClick, loading, disabled, children = "Continue with Google" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      className="w-full h-12 rounded-xl border border-brand-border bg-white text-brand-text font-semibold flex items-center justify-center gap-3 hover:bg-brand-surface2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-accent"
    >
      {loading ? (
        <Loader2 size={18} className="animate-spin" aria-hidden="true" />
      ) : (
        <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
      )}
      {children}
    </button>
  );
}
